import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChatCircle,
  Shield,
  ArrowUp,
  ArrowRight,
  TrendUp,
  UsersThree,
  Calculator,
  BookOpen,
  ShieldCheck,
} from "@phosphor-icons/react";
import RomyIcon from "../components/RomyIcon";
import UserMessage from "../components/chat/UserMessage";
import AssistantMessage from "../components/chat/AssistantMessage";
import LoadingDots from "../components/chat/LoadingDots";
import MarkdownContent from "../components/chat/MarkdownContent";
import AppNav from "../components/AppNav";
import AppFooter from "../components/AppFooter";
import PageMeta from "../components/PageMeta";
import JsonLd, { organizationSchema, webApplicationSchema, faqSchema } from "../components/JsonLd";
import PhoneMockup from "../components/landing/PhoneMockup";
import HowItWorks from "../components/landing/HowItWorks";
import FAQAccordion from "../components/landing/FAQAccordion";
import StickyMobileCTA from "../components/landing/StickyMobileCTA";
import { useAuth } from "../contexts/AuthContext";
import { QUIZZES } from "../data/quizData";
import { CONDITION_PAGES } from "../data/conditionPages";
import type { TrendingTheme } from "@researcher/shared";
import { fetchCommunityInsights } from "../utils/communityInsights";
import { apiUrl } from "../config/api";

/** Strip RAG citation brackets like [1], [1, 5] from preview text */
const stripCitations = (text: string) =>
  text.replace(/\s*\[\d+(?:,\s*\d+)*\]/g, '');

const SELF_SEGMENTATION_CHIPS = [
  "I have endometriosis",
  "PCOS symptoms",
  "Trying to conceive",
  "Period pain help",
  "Prepare for my doctor visit",
];

const FAQ_ITEMS = [
  {
    question: "What is Romy?",
    answer:
      "Romy (pronounced roe-mee) is a free AI-powered women's health companion that provides personalized insights for conditions like endometriosis, PCOS, PMDD, and more.",
  },
  {
    question: "Is Romy free to use?",
    answer:
      "Yes, Romy is completely free. You can chat with our AI, track symptoms, join the community, and access all health tools at no cost.",
  },
  {
    question: "What conditions does Romy cover?",
    answer:
      "Romy covers endometriosis, PCOS, PMDD, perimenopause, adenomyosis, fibroids, thyroid disorders, interstitial cystitis, vulvodynia, postpartum health, and IVF/fertility.",
  },
  {
    question: "Is Romy a replacement for medical advice?",
    answer:
      "No. Romy provides educational health information and tools to help you prepare for doctor visits, but is not a substitute for professional medical advice.",
  },
  {
    question: "Is my health data private?",
    answer:
      "Absolutely. Your data is encrypted, never sold, and you can export or delete it at any time. We follow strict privacy practices — you stay in full control.",
  },
  {
    question: "How does Romy find research?",
    answer:
      "Romy draws from over 1,000 clinical sources including PubMed-backed research, evidence-classified articles, and curated condition-specific knowledge reviewed for accuracy.",
  },
];

const TESTIMONIALS = [
  {
    quote:
      "Romy helped me see the connection between my cycle and my worst symptom days. I walked into my specialist appointment with actual data instead of 'it just hurts a lot.'",
    name: "Sarah K.",
    condition: "Endometriosis",
    emoji: "🌸",
  },
  {
    quote:
      "The visit prep feature is incredible. My gynecologist said it was the most organized patient summary she'd ever seen.",
    name: "Maria T.",
    condition: "PCOS",
    emoji: "💪",
  },
  {
    quote:
      "Finally, an AI that understands what I'm going through without me having to explain everything from scratch every time.",
    name: "Jamie L.",
    condition: "PMDD",
    emoji: "💜",
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const [trendingThemes, setTrendingThemes] = useState<TrendingTheme[]>([]);
  const [previewQuery, setPreviewQuery] = useState("");
  const [previewResponse, setPreviewResponse] = useState("");
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const fullResponseRef = useRef("");

  useEffect(() => {
    fetchCommunityInsights()
      .then((data) => {
        if (data?.trendingThemes?.length) {
          setTrendingThemes(data.trendingThemes.slice(0, 6));
        }
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e?: React.FormEvent, chipText?: string) => {
    if (e) e.preventDefault();
    const msg = (chipText || input).trim();
    if (!msg || isPreviewLoading) return;

    setPreviewQuery(msg);
    setPreviewResponse("");
    fullResponseRef.current = "";
    setIsPreviewLoading(true);
    if (!chipText) setInput("");

    const MAX_DISPLAY_CHARS = 400;

    try {
      abortRef.current = new AbortController();
      const headers: HeadersInit = { "Content-Type": "application/json" };

      const response = await fetch(apiUrl("/chat"), {
        method: "POST",
        headers,
        body: JSON.stringify({ message: msg, conversationHistory: [] }),
        signal: abortRef.current.signal,
      });

      if (!response.ok || !response.body) {
        navigate(`/chat?message=${encodeURIComponent(msg)}`);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.chunk) {
              fullResponseRef.current += parsed.chunk;
              const clean = stripCitations(fullResponseRef.current);
              if (clean.length <= MAX_DISPLAY_CHARS) {
                setPreviewResponse(clean);
              } else {
                setPreviewResponse(clean.slice(0, MAX_DISPLAY_CHARS) + "...");
              }
            }
          } catch {
            /* skip */
          }
        }
      }
      if (!fullResponseRef.current) {
        setPreviewResponse("Let me tell you more in the full chat...");
      }
    } catch (err: any) {
      if (err?.name === "AbortError") return;
      navigate(`/chat?message=${encodeURIComponent(msg)}`);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 pb-32 md:pb-0">
      <PageMeta
        title="Romy — Your AI Women's Health Companion"
        description="AI-powered women's health companion for endometriosis, PCOS, PMDD, fertility, and more. Track symptoms, get personalized insights, prepare for doctor visits, and feel understood."
        path="/"
      />
      <JsonLd
        data={[
          organizationSchema,
          webApplicationSchema,
          faqSchema(FAQ_ITEMS),
        ]}
      />
      <AppNav />

      <main className="pt-16 min-h-screen">
        {/* ─── Section 1: Hero — two-column with phone on right ─── */}
        <section
          ref={heroRef}
          className="bg-gradient-to-b from-orange-50 to-white dark:from-gray-900 dark:to-gray-900 py-10 sm:py-16 lg:py-24"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left — copy */}
              <div className="text-center md:text-left">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 dark:text-gray-100 leading-tight mb-2 lg:mb-3">
                  Meet <span className="text-pink-500">Romy</span>, Your AI
                  Health Companion
                </h1>
                <p className="text-base sm:text-lg text-gray-400 dark:text-gray-500 italic mb-4 lg:mb-6">/roe-mee/</p>
                <p className="text-base sm:text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6 lg:mb-8 max-w-lg mx-auto md:mx-0">
                  Research-backed answers for endometriosis, PCOS, PMDD,
                  fertility, and more. Free, private, and grounded in clinical
                  evidence.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start mb-6">
                  <Link
                    to="/chat"
                    className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all shadow-lg hover:shadow-xl"
                  >
                    <ChatCircle size={20} weight="fill" className="mr-2" />
                    Chat with Romy
                  </Link>
                  <Link
                    to="/research"
                    className="inline-flex items-center justify-center px-6 py-3 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-semibold rounded-lg border-2 border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 transition-all"
                  >
                    <BookOpen size={20} weight="regular" className="mr-2" />
                    Explore Research
                  </Link>
                </div>
                {/* Trust badges inline */}
                <div className="flex flex-wrap gap-4 justify-center md:justify-start text-xs text-gray-500 dark:text-gray-400">
                  <Link to="/trust" className="flex items-center gap-1.5 hover:text-pink-500 transition-colors">
                    <BookOpen size={14} weight="fill" className="text-pink-500" />
                    1,000+ clinical sources
                  </Link>
                  <Link to="/trust" className="flex items-center gap-1.5 hover:text-pink-500 transition-colors">
                    <ShieldCheck size={14} weight="fill" className="text-pink-500" />
                    Private & encrypted
                  </Link>
                  <Link to="/trust" className="flex items-center gap-1.5 hover:text-pink-500 transition-colors">
                    <Shield size={14} weight="fill" className="text-pink-500" />
                    Your data, your control
                  </Link>
                </div>
              </div>

              {/* Right — phone mockup with chat */}
              <PhoneMockup>
                <div
                  className="bg-white dark:bg-gray-900 rounded-xl sm:rounded-2xl md:rounded-none shadow-xl md:shadow-none p-4 flex flex-col md:h-full"
                  style={{ minHeight: 280 }}
                >
                  {previewQuery ? (
                    <div className="flex-1 overflow-y-auto space-y-4 mb-3">
                      <UserMessage content={previewQuery} />
                      {previewResponse ? (
                        <AssistantMessage>
                          <MarkdownContent
                            content={previewResponse}
                            className="text-sm leading-relaxed prose prose-sm max-w-none prose-headings:text-gray-800 dark:prose-headings:text-gray-100 prose-p:text-gray-800 dark:prose-p:text-gray-100 prose-strong:text-gray-900 dark:prose-strong:text-gray-100 prose-ul:text-gray-800 dark:prose-ul:text-gray-100 prose-ol:text-gray-800 dark:prose-ol:text-gray-100 prose-li:text-gray-800 dark:prose-li:text-gray-100 prose-a:text-pink-600 dark:prose-a:text-pink-400"
                          />
                        </AssistantMessage>
                      ) : (
                        <LoadingDots />
                      )}
                      {!isPreviewLoading && previewResponse && (
                        <div className="flex justify-center pt-2">
                          <button
                            onClick={() =>
                              navigate(
                                `/chat?message=${encodeURIComponent(previewQuery)}&preview=${encodeURIComponent(stripCitations(fullResponseRef.current))}`
                              )
                            }
                            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-medium rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all shadow-md"
                          >
                            Continue conversation
                            <ArrowRight size={14} weight="bold" />
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-center px-2 mb-3">
                      <div className="mb-3 text-pink-500">
                        <RomyIcon size={40} weight="fill" />
                      </div>
                      <h2 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">
                        What's on your mind?
                      </h2>
                      <div className="flex flex-wrap justify-center gap-1.5 mb-2">
                        {SELF_SEGMENTATION_CHIPS.map((chip) => (
                          <button
                            key={chip}
                            onClick={() => handleSubmit(undefined, chip)}
                            className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-pink-200 dark:border-pink-800 hover:border-pink-400 dark:hover:border-pink-600 hover:shadow-sm transition-all"
                          >
                            {chip}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <form
                    onSubmit={(e) => handleSubmit(e)}
                    className="mt-auto flex-shrink-0"
                  >
                    <div className="relative border border-gray-200 dark:border-gray-700 rounded-2xl bg-white dark:bg-gray-800 shadow-sm focus-within:border-pink-400 dark:focus-within:border-pink-500 focus-within:ring-1 focus-within:ring-pink-400/30 transition-all">
                      <textarea
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyPress}
                        placeholder="Message Romy..."
                        className="block w-full resize-none bg-transparent px-3 py-2 pr-10 text-sm text-gray-800 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none"
                        rows={1}
                        style={{ minHeight: 38, maxHeight: 80 }}
                      />
                      {input.trim() && !isPreviewLoading && (
                        <button
                          type="submit"
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 text-white hover:from-orange-600 hover:to-pink-600 shadow-sm transition-all"
                        >
                          <ArrowUp size={16} weight="bold" />
                        </button>
                      )}
                    </div>
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1.5 text-center">
                      Romy is your advocate, not your doctor.
                    </p>
                  </form>
                </div>
              </PhoneMockup>
            </div>
          </div>
        </section>

        {/* ─── Section 2: Trending Themes ─── */}
        {trendingThemes.length > 0 && (
          <section className="py-6 sm:py-8 bg-white dark:bg-gray-900">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-2 mb-3">
                  <UsersThree
                    size={18}
                    className="text-pink-500"
                    weight="fill"
                  />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Trending in our community
                  </span>
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  {trendingThemes.map((theme, i) => (
                    <Link
                      key={i}
                      to={`/community/${theme.topicSlug}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-pink-200 dark:border-pink-800 hover:border-pink-400 dark:hover:border-pink-600 hover:shadow-sm transition-all"
                    >
                      <TrendUp size={12} className="text-orange-500" />
                      {theme.theme}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ─── Section 3: Stats ─── */}
        <section className="py-10 sm:py-14 bg-pink-50 dark:bg-gray-800/50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                {
                  value: "1,000+",
                  label: "Clinical Sources",
                  sub: "PubMed-backed knowledge",
                },
                {
                  value: "100+",
                  label: "Deep-Dive Topics",
                  sub: "PubMed-backed research libraries",
                },
                {
                  value: "24/7",
                  label: "AI Health Companion",
                  sub: "Always here for you",
                },
                {
                  value: "Prepared",
                  label: "For Every Visit",
                  sub: "Walk in informed, not dismissed",
                },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="text-center bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-6 border border-pink-100 dark:border-pink-900/40 shadow-sm"
                >
                  <div className="text-2xl sm:text-3xl font-bold text-pink-500">
                    {stat.value}
                  </div>
                  <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 mt-1">
                    {stat.label}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {stat.sub}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── Section 4: How It Works ─── */}
        <HowItWorks />

        {/* ─── Section 5: Start Your Health Journey ─── */}
        <section className="py-12 sm:py-16 bg-pink-50 dark:bg-gray-800/50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div className="flex flex-col justify-center space-y-6">
                <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
                  Start Your Health Journey Today
                </h2>
                <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                  Ask Romy anything about women's health. Get evidence-based
                  information, explore treatment options, and find resources to
                  support your wellness journey.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/get-started"
                    className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all shadow-lg hover:shadow-xl"
                  >
                    <ArrowRight size={20} weight="bold" className="mr-2" />
                    Get Started — 2 Min Quiz
                  </Link>
                  <Link
                    to="/community"
                    className="inline-flex items-center justify-center px-6 py-3 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 font-semibold rounded-lg border-2 border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 transition-all"
                  >
                    <UsersThree size={20} weight="regular" className="mr-2" />
                    Join the Community
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── Section 6: Testimonials ─── */}
        <section className="py-12 sm:py-16 bg-white dark:bg-gray-900">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-100 text-center mb-10 sm:mb-14">
              What Women Are Saying
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {TESTIMONIALS.map((t) => (
                <div
                  key={t.name}
                  className="relative bg-pink-50 dark:bg-gray-800 rounded-2xl p-6 sm:p-8 border border-pink-100 dark:border-pink-900/40"
                >
                  <span className="text-3xl mb-3 block">{t.emoji}</span>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 leading-relaxed mb-5">
                    "{t.quote}"
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-pink-200 dark:bg-pink-800 flex items-center justify-center text-pink-600 dark:text-pink-300 text-xs font-bold">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                        {t.name}
                      </div>
                      <div className="text-xs text-pink-500">{t.condition}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-6">
              Names changed for privacy
            </p>
          </div>
        </section>

        {/* ─── Section 7: Conditions & Topics ─── */}
        <section className="py-12 sm:py-16 bg-pink-50 dark:bg-gray-800/50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2 text-center">
              Conditions & Topics
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 mb-6 sm:mb-8 text-center">
              Evidence-based guides and conversations
            </p>
            {/* Condition guide pills */}
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              {CONDITION_PAGES.map((condition) => (
                <Link
                  key={condition.slug}
                  to={`/conditions/${condition.slug}`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 rounded-full border border-pink-200 dark:border-pink-800 hover:border-pink-400 dark:hover:border-pink-600 hover:shadow-md transition-all text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  <span>{condition.heroEmoji}</span>
                  {condition.name}
                </Link>
              ))}
            </div>
            {/* Topic chat links */}
            <div className="flex flex-wrap justify-center gap-2">
              {[
                "Mental Health",
                "GLP-1s",
                "Birth Control",
                "Chronic Pain",
                "IVF & Fertility",
                "Perimenopause",
              ].map((topic) => (
                <Link
                  key={topic}
                  to={`/chat?topic=${encodeURIComponent(topic)}`}
                  className="px-3 py-1.5 rounded-full text-xs font-medium bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:border-pink-300 dark:hover:border-pink-700 hover:text-gray-800 dark:hover:text-gray-200 transition-all"
                >
                  {topic}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ─── Section 8: Free Health Quizzes ─── */}
        <section className="py-12 sm:py-16 bg-white dark:bg-gray-900">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <div className="text-4xl mb-3">✨</div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-3 sm:mb-4">
                Free Health Quizzes
              </h2>
              <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                Take our self-check quizzes to understand your symptoms and get
                personalized insights
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {QUIZZES.map((quiz) => (
                <Link
                  key={quiz.slug}
                  to={`/quiz?type=${quiz.slug}`}
                  className={`bg-gradient-to-r ${quiz.gradientFrom} ${quiz.gradientTo} rounded-2xl shadow-xl p-6 sm:p-8 text-white hover:shadow-2xl transition-all`}
                >
                  <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">
                    {quiz.emoji}
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 line-clamp-2">
                    {quiz.title}
                  </h3>
                  <p className="text-white/90 text-sm mb-4 line-clamp-2">
                    {quiz.subtitle}
                  </p>
                  <div className="inline-flex items-center space-x-2 text-white font-semibold text-sm">
                    <span>Take Quiz</span>
                    <ArrowRight size={18} weight="bold" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ─── Section 9: Free Health Calculators ─── */}
        <section className="py-12 sm:py-16 bg-pink-50 dark:bg-gray-800/50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-3">
                <Calculator size={36} className="text-pink-500" weight="fill" />
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-3 sm:mb-4">
                Free Health Calculators
              </h2>
              <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                Quick tools to understand your body better
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {[
                { name: "Period Calculator", path: "/tools/period-calculator", emoji: "\uD83D\uDCC5" },
                { name: "Ovulation Calculator", path: "/tools/ovulation-calculator", emoji: "\uD83E\uDD5A" },
                { name: "Due Date Calculator", path: "/tools/due-date-calculator", emoji: "\uD83D\uDC76" },
                { name: "BMI Calculator", path: "/tools/bmi-calculator", emoji: "\uD83D\uDCCA" },
              ].map((tool) => (
                <Link
                  key={tool.path}
                  to={tool.path}
                  className="flex flex-col items-center p-4 sm:p-6 bg-white dark:bg-gray-800 rounded-2xl border border-pink-100 dark:border-pink-900/40 hover:border-pink-300 dark:hover:border-pink-700 hover:shadow-lg transition-all text-center"
                >
                  <span className="text-2xl sm:text-3xl mb-2">
                    {tool.emoji}
                  </span>
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {tool.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ─── Section 10: FAQ ─── */}
        <section className="py-12 sm:py-16 bg-white dark:bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-100 text-center mb-8 sm:mb-10">
              Frequently Asked Questions
            </h2>
            <FAQAccordion items={FAQ_ITEMS} />
          </div>
        </section>

        {/* ─── Section 11: Final CTA ─── */}
        <section className="py-12 sm:py-16 bg-gradient-to-b from-pink-50 to-white dark:from-gray-800/50 dark:to-gray-900 text-center">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center mb-3 sm:mb-4 text-pink-500">
              <RomyIcon size={36} weight="fill" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-3 sm:mb-4">
              Ready to Take Control of Your Health?
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 dark:text-gray-400 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Empower yourself with knowledge and AI-powered support tailored to
              women's health.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/chat"
                className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all shadow-lg hover:shadow-xl"
              >
                <ChatCircle size={24} weight="fill" className="mr-2" />
                Start Chatting with Romy
              </Link>
              <Link
                to="/advocacy"
                className="inline-flex items-center justify-center px-8 py-4 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 font-semibold rounded-lg border-2 border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 transition-all"
              >
                <Shield size={24} weight="regular" className="mr-2" />
                Advocacy Tools
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Sticky mobile CTA for non-auth users */}
      {!currentUser && <StickyMobileCTA heroRef={heroRef} />}

      <AppFooter />
    </div>
  );
}
